// Mind › Words into maps, the view (features/text.ts runs ba-mind's model): a topic box with example topics, the
// terms the model recognised (or that none were, the map then cleared), the strongest prediction and where it is,
// the ±8 legend; and over the viewport a board of three native slices through the model's own 4 mm grid, each with
// its slider and mm, orientation letters at the edges, a crosshair where the other two slices pass, and a click to
// move them. A still view: the scene's clock and run stand aside (app/scene.ts), the hemispheres are hidden by the
// preset, and nothing is sent to the cortex.

import { useEffect, useRef, useState } from 'react';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { Button, Section, Slider } from '../ui/primitives.tsx';
import { EXAMPLES, MAX_QUERY, SLICES, mmOf, pixelOf, sliceSize, slicePixels, useText, voxelAt, type Axis, type TextStatus } from './text.ts';
import { CORTEX_FACTS, CORTEX_RUN, cortexLine, supportedOf, useTextCortex, useTextCortexLayer } from './textCortex.ts';
import './web.css';

export const textActive = (s: Pick<AtlasState, 'scenario' | 'preset'>): boolean => s.scenario === 'mind' && s.preset === 'text';

/** The gap between the board and the panels around it, px. */
const GAP = 16;
/** The gap between the strip and the bottom row it clears on a phone, px (the panels' gap). */
const BOTTOM_GAP = 12;

/** The panel's section: the topic, its terms, the peak and the legend; the model starts loading when the view opens. */
export function TextSections() {
  const active = useAtlas(textActive);
  const open = useText((s) => s.open);
  useEffect(() => {
    if (active) open();
  }, [active, open]);
  if (!active) return null;
  return <TopicSection />;
}

function recognisedLine(status: TextStatus, error: string | null, query: string, terms: string[], ignored: string[] = []): string {
  if (status === 'loading' || status === 'idle') return 'Loading the model, 27 MB, once…';
  if (status === 'missing') return 'The text model isn’t on this server (sims/mind/neuroquery-native-v1).';
  if (status === 'error') return `Couldn’t load the model: ${error ?? 'unknown'}`;
  if (error) return `Couldn’t map that: ${error}`;
  // The words the model read nothing from are named, so “not pain” shows “not” dropped: the model reads terms, not sentences.
  const dropped = ignored.length ? ` · ignored: ${ignored.join(', ')}` : '';
  if (terms.length) return `Recognised: ${terms.join(' · ')}${dropped}`;
  return query ? 'No terms recognised: the map is cleared. Try one of the topics above.' : 'Type a topic, or pick one above.';
}

const signed = (v: number, digits: number): string => `${v < 0 ? '−' : '+'}${Math.abs(v).toFixed(digits)}`;

function TopicSection() {
  const status = useText((s) => s.status);
  const error = useText((s) => s.error);
  const query = useText((s) => s.query);
  const terms = useText((s) => s.terms);
  const ignored = useText((s) => s.ignored);
  const peak = useText((s) => s.peak);
  const submit = useText((s) => s.submit);
  const [draft, setDraft] = useState(query);
  // A chip or a link changes the topic under the box.
  useEffect(() => setDraft(query), [query]);
  return (
    <Section title="Words into maps">
      <form
        className="text-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit(draft);
        }}
      >
        <input
          className="text-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          placeholder="A topic, like working memory"
          aria-label="Topic"
          maxLength={MAX_QUERY}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
        />
        <Button type="submit" disabled={status !== 'ready'}>
          Show map
        </Button>
      </form>
      <div className="text-chips" role="group" aria-label="Example topics">
        {EXAMPLES.map((e) => (
          <button key={e.q} type="button" className={`text-chip${query === e.q ? ' on' : ''}`} onClick={() => submit(e.q)} aria-pressed={query === e.q}>
            {e.label}
          </button>
        ))}
      </div>
      <p className="text-recognised ba-type-small" aria-live="polite">
        {recognisedLine(status, error, query, terms, ignored)}
      </p>
      <CortexLine />
      {peak ? (
        <dl className="text-facts">
          <dt>Peak</dt>
          <dd>
            <span className="num">{signed(peak.value, 1)}</span>
            {` at x ${peak.mm[0]}, y ${peak.mm[1]}, z ${peak.mm[2]} mm`}
          </dd>
        </dl>
      ) : null}
      <div className="text-legend" role="img" aria-label="Predicted standardized score, −8 to +8; slate near zero">
        <span className="text-strip" />
        <span className="text-ticks ba-type-caption">
          <span>−8</span>
          <span>0</span>
          <span>+8</span>
        </span>
        <span className="text-key ba-type-caption">Predicted standardized score · slate: near zero</span>
      </div>
      <p className="ba-type-caption muted text-note">
        Where the neuroimaging literature on a topic reports activity, as NeuroQuery predicts it, on the cortex through ba-mind’s conditional projection to fsaverage5 (18,474 supported vertices; the rest, the medial wall and the subcortex stay bare) and on the model’s own 4 mm grid in the slices. The model reads terms, not sentences: stop words and word order are dropped, so “not pain” maps as “pain”, and the words it didn’t use are named. The
        slices follow the strongest prediction when the topic changes. The model loads once and runs in this browser.
      </p>
    </Section>
  );
}

/** The three slices over the viewport (App.tsx mounts it inside the stage, under the panels), in the clear area the
 *  panels leave; nothing until the view is on. */
export function TextBoard() {
  const active = useAtlas(textActive);
  const right = useAtlas((s) => s.insets.right);
  const left = useAtlas((s) => s.insets.left);
  const rowClear = useBottomRow(active);
  const status = useText((s) => s.status);
  const error = useText((s) => s.error);
  const grid = useText((s) => s.grid);
  const mask = useText((s) => s.mask);
  // The topic on the cortex rides along while the view is on (features/textCortex.ts).
  useTextCortexLayer(active);
  if (!active) return null;
  // A strip along the bottom of the clear area, the cortex above it; it counts toward the bottom inset so the
  // framing leaves it room (App.tsx measures [data-inset]).
  const style = rowClear === null ? { right: right + GAP, left: left + GAP } : { right: right + GAP, left: left + GAP, bottom: rowClear };
  if (!grid || !mask) {
    return (
      <div className="text-board strip" data-label-exclude data-inset="bottom" style={style}>
        <p className="text-board-note ba-type-small">{recognisedLine(status, error, '', [])}</p>
      </div>
    );
  }
  return (
    <div className="text-board strip" data-label-exclude data-inset="bottom" style={style}>
      <div className="text-slices">
        {SLICES.map((sl) => (
          <SliceCard key={sl.axis} axis={sl.axis} name={sl.name} letter={sl.letter} dirs={sl.dirs} />
        ))}
      </div>
    </div>
  );
}

/** On a phone (features/phone.css, under 700 px) the bottom row is one column over the stage, and in this still view it holds
 *  the Methods caption alone, so the strip measures the row and sits a gap above it; the row's height follows the caption's
 *  lines. Wider screens return null: the row's middle column stands empty under the strip (no clock, no traces), so the
 *  strip's own 12 px bottom is right. */
function useBottomRow(active: boolean): number | null {
  const [clear, setClear] = useState<number | null>(null);
  useEffect(() => {
    if (!active) return;
    const mq = window.matchMedia('(max-width: 700px)');
    const row = document.querySelector<HTMLElement>('.atlas .bottom');
    const stage = row?.offsetParent;
    if (!row || !stage) return;
    const measure = () => {
      if (!mq.matches) return setClear(null);
      setClear(Math.round(stage.getBoundingClientRect().bottom - row.getBoundingClientRect().top) + BOTTOM_GAP);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    mq.addEventListener('change', measure);
    measure();
    return () => {
      ro.disconnect();
      mq.removeEventListener('change', measure);
    };
  }, [active]);
  return clear;
}

/** The panel's line on the cortex: drawn through the layer, loading, or why the slices stand alone. */
function CortexLine() {
  const status = useTextCortex((s) => s.status);
  const error = useTextCortex((s) => s.error);
  const layer = useTextCortex((s) => s.layer);
  const covered = useTextCortex((s) => s.covered);
  const model = useTextCortex((s) => s.model);
  return (
    <p className="text-cortex ba-type-caption muted" aria-live="polite">
      {cortexLine({ status, error, layer, covered, model })}
    </p>
  );
}

function SliceCard({ axis, name, letter, dirs }: { axis: Axis; name: string; letter: 'x' | 'y' | 'z'; dirs: [string, string, string, string] }) {
  const grid = useText((s) => s.grid)!;
  const mask = useText((s) => s.mask)!;
  const volume = useText((s) => s.volume);
  const slice = useText((s) => s.slice);
  const setSlice = useText((s) => s.setSlice);
  const setPoint = useText((s) => s.setPoint);
  const canvas = useRef<HTMLCanvasElement>(null);
  const index = slice[axis];
  const { width, height } = sliceSize(grid.shape, axis);
  useEffect(() => {
    const c = canvas.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const px = slicePixels(volume, mask, grid.shape, axis, index);
    ctx.putImageData(new ImageData(px.data, px.width, px.height), 0, 0);
  }, [volume, mask, grid, axis, index]);
  const cross = pixelOf(grid.shape, axis, slice);
  const mm = mmOf(grid.affine, slice)[axis];
  return (
    <section className="text-slice" aria-label={`${name} slice`}>
      <header className="text-slice-head">
        <h3 className="ba-type-overline">{name}</h3>
        <span className="text-mm ba-type-caption">{`${letter} = ${mm} mm`}</span>
      </header>
      <div className="text-pic" style={{ aspectRatio: `${width} / ${height}` }}>
        <canvas
          ref={canvas}
          width={width}
          height={height}
          role="img"
          aria-label={`${name} slice at ${letter} = ${mm} mm`}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const col = Math.min(width - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * width)));
            const row = Math.min(height - 1, Math.max(0, Math.floor(((e.clientY - r.top) / r.height) * height)));
            setPoint(voxelAt(grid.shape, axis, col, row, slice));
          }}
        />
        <span className="text-cross v" style={{ left: `${((cross.col + 0.5) / width) * 100}%` }} />
        <span className="text-cross h" style={{ top: `${((cross.row + 0.5) / height) * 100}%` }} />
        <span className="text-dir l">{dirs[0]}</span>
        <span className="text-dir r">{dirs[1]}</span>
        <span className="text-dir t">{dirs[2]}</span>
        <span className="text-dir b">{dirs[3]}</span>
      </div>
      <label className="row text-slider">
        <span>Slice</span>
        <Slider value={index} min={0} max={grid.shape[axis] - 1} step={1} onChange={(v) => setSlice(axis, v)} label={`${name} slice`} />
        <span className="num value">{index + 1}</span>
      </label>
    </section>
  );
}

/** The Methods sheet's section for the view: the model, its grid and space, its fidelity, its licence. */
export function TextMethods() {
  const header = useText((s) => s.header);
  const status = useText((s) => s.status);
  if (!header) return <p className="muted">{status === 'missing' ? 'The text model isn’t on this server.' : 'Loading the text model…'}</p>;
  const shape = header.grid.shape;
  return (
    <section className="methods-sim methods-text">
      <h3>Words into maps · NeuroQuery</h3>
      {typeof header.methods === 'string' ? <p>{header.methods}</p> : null}
      <dl>
        <dt>Model</dt>
        <dd>
          <span className="mono">{header.id}</span> <span className="muted">{header.model}</span>
        </dd>
        <dt>Grid</dt>
        <dd>{`${shape[0]} × ${shape[1]} × ${shape[2]} voxels at ${header.grid.affine_ras_mm[0]?.[0] ?? 4} mm, ${header.voxels.toLocaleString()} in the mask · ${header.space}`}</dd>
        <dt>Template</dt>
        <dd>{`${header.coordinate_system}. ba-mind’s audit (MIND-003A) identified the mask’s reference grid as MNI152NLin6Asym; cortical projection of this native run ${header.projection.status} (${header.projection.reason}); the cortex takes the separate MIND-003C projection, below`}</dd>
        <dt>Vocabulary</dt>
        <dd>{`${header.vocabulary_size.toLocaleString()} terms, ${header.features} regression features; terms, not sentences: stop words dropped and word order ignored, so “not pain” maps as “pain”`}</dd>
        <dt>Fidelity</dt>
        <dd>ba-mind’s local encoder against the Python model: aggregate RMSE 8.3 × 10⁻⁸ over 24 queries (MIND-003)</dd>
        <dt>Scale</dt>
        <dd>±8 standardized score, a display choice; the peak is shown unclipped</dd>
        <dt>Licence</dt>
        <dd>NeuroQuery, BSD 3-Clause (Jérôme Dockès); sims/mind/neuroquery-LICENSE-v1.txt</dd>
      </dl>
    </section>
  );
}

/** The cortex model's section: what the projection is, what it passed and what it didn't, and its limits. */
export function TextCortexMethods() {
  const status = useTextCortex((s) => s.status);
  const error = useTextCortex((s) => s.error);
  const layer = useTextCortex((s) => s.layer);
  const covered = useTextCortex((s) => s.covered);
  const model = useTextCortex((s) => s.model);
  const header = model?.header ?? null;
  return (
    <section className="methods-sim methods-text methods-text-cortex">
      <h3>On the cortex · MIND-003C</h3>
      {header && typeof header.methods === 'string' ? <p>{header.methods}</p> : null}
      <p>
        {`ba-mind’s conditional projection of the same NeuroQuery model to fsaverage5: ${CORTEX_FACTS.vertices.toLocaleString()} vertices, ${supportedOf(model).toLocaleString()} of them supported, the rest left bare (NaN), with the medial wall and the subcortex. It passed a conditional fidelity test only: on the pre-existing brain support the browser model matches the reference projection to an aggregate RMSE of ${CORTEX_FACTS.rmse} over ${CORTEX_FACTS.queries} queries, where a zero map scores ${CORTEX_FACTS.zero}. The whole-head template-identity test (MIND-003B) failed, so nothing is claimed beyond that support, and no biological accuracy is claimed: a 4 mm source, no subcortex or cerebellum, negation unread, and literature peaks registered heterogeneously.`}
      </p>
      <dl>
        <dt>Model</dt>
        <dd>
          <span className="mono">{header?.id ?? CORTEX_RUN.split('/')[1]}</span> <span className="muted">{header?.model ?? ''}</span>
        </dd>
        <dt>Projection</dt>
        <dd>{header ? `${header.projection.source_space} → ${header.projection.target_space}, ${header.projection.valid_vertices.toLocaleString()} supported vertices` : `MNI152NLin6Asym → fsaverage5, ${CORTEX_FACTS.supported.toLocaleString()} supported vertices`}</dd>
        <dt>On the cortex</dt>
        <dd>{layer === 'pushed' ? `${CORTEX_FACTS.vertices.toLocaleString()} values${covered?.of ? ` on ${covered.of.toLocaleString()} displayed vertices` : ''}, through the viewport’s fsaverage5 map; the look is the signed change scale at ±8, slate near zero, bare where the model has no vertex` : layer === 'none' ? 'Not drawn by this viewport' : status === 'missing' ? 'The cortex model isn’t on this server' : status === 'error' ? `The cortex model didn’t load: ${error ?? ''}` : 'Waiting for the model and the viewport'}</dd>
        <dt>Licence</dt>
        <dd>NeuroQuery, BSD 3-Clause (Jérôme Dockès); sims/mind/neuroquery-cortex-v1/LICENSE.neuroquery</dd>
      </dl>
    </section>
  );
}
